import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  Sparkles, 
  TrendingUp, 
  Target, 
  Users, 
  BookOpen, 
  Percent,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  Trophy,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BettingSuggestions = () => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/reports/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Unable to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-dark border-white/10 mb-6">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
            <span className="text-gray-400">Analyzing your betting patterns...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Silently fail - don't block the page
  }

  if (!suggestions) return null;

  // Insufficient data state
  if (suggestions.status === 'insufficient_data') {
    return (
      <Card className="glass-dark border-white/10 mb-6 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500" />
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Betting Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <BarChart3 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-300 text-sm">
                {suggestions.message}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Current: {suggestions.total_bets} bets • Need: {suggestions.minimum_required} minimum
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { confidence, confidence_message, top_recommendations } = suggestions;
  const data = suggestions.suggestions;

  // Render a single suggestion category
  const renderSuggestionItem = (icon, label, categoryData, colorClass) => {
    if (!categoryData?.has_data || !categoryData?.best) return null;
    
    const best = categoryData.best;
    const isPositiveROI = best.roi > 0;
    
    return (
      <div className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <span className="text-gray-400 text-sm font-medium">{label}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white font-semibold truncate max-w-[60%]" title={best.name}>
            {best.name}
          </span>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${isPositiveROI ? 'text-green-400' : 'text-red-400'}`}>
              {isPositiveROI ? '+' : ''}{best.roi}% ROI
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span>{best.bets} bets</span>
          <span>{best.win_rate}% win rate</span>
        </div>
      </div>
    );
  };

  // Render detailed stats table for expanded view
  const renderDetailedStats = (label, categoryData, icon) => {
    if (!categoryData?.has_data || categoryData.all.length === 0) return null;
    
    return (
      <div className="mt-4">
        <h4 className="text-gray-300 text-sm font-medium mb-2 flex items-center gap-2">
          {icon}
          {label} Performance
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-white/10">
                <th className="text-left py-2 pr-2">Name</th>
                <th className="text-center py-2 px-2">Bets</th>
                <th className="text-center py-2 px-2">W/L</th>
                <th className="text-center py-2 px-2">Win %</th>
                <th className="text-right py-2 pl-2">ROI</th>
              </tr>
            </thead>
            <tbody>
              {categoryData.all.map((item, index) => (
                <tr key={index} className="border-b border-white/5">
                  <td className="py-2 pr-2 text-white truncate max-w-[150px]" title={item.name}>
                    {index === 0 && <Trophy className="w-3 h-3 text-yellow-400 inline mr-1" />}
                    {item.name}
                  </td>
                  <td className="py-2 px-2 text-center text-gray-400">{item.bets}</td>
                  <td className="py-2 px-2 text-center">
                    <span className="text-green-400">{item.wins}</span>
                    <span className="text-gray-600">/</span>
                    <span className="text-red-400">{item.losses}</span>
                  </td>
                  <td className="py-2 px-2 text-center text-gray-300">{item.win_rate}%</td>
                  <td className={`py-2 pl-2 text-right font-medium ${item.roi > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {item.roi > 0 ? '+' : ''}{item.roi}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Card className="glass-dark border-white/10 mb-6 overflow-hidden relative">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-purple-500" />
      
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Betting Analysis
          </CardTitle>
          <div className="flex items-center gap-2">
            {confidence === 'low' && (
              <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">
                <AlertTriangle className="w-3 h-3" />
                Low Data
              </span>
            )}
            <span className="text-xs text-gray-500">
              {suggestions.total_bets} bets analyzed
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Confidence warning for low data */}
        {confidence === 'low' && (
          <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-yellow-200 text-xs">
              {confidence_message} Track at least 20 bets for more reliable suggestions.
            </p>
          </div>
        )}

        {/* Quick Recommendations */}
        {top_recommendations && top_recommendations.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
            <p className="text-xs text-purple-300 font-medium mb-2 flex items-center gap-1">
              <Target className="w-3 h-3" />
              Top Recommendations
            </p>
            <div className="space-y-1">
              {top_recommendations.map((rec, index) => (
                <p key={index} className="text-sm text-white flex items-center gap-2">
                  <span className="text-green-400">→</span> {rec}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Compact Suggestions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {renderSuggestionItem(
            <Target className="w-4 h-4 text-green-400" />,
            "Best Market",
            data?.market,
            "green"
          )}
          {renderSuggestionItem(
            <TrendingUp className="w-4 h-4 text-blue-400" />,
            "Best League",
            data?.league,
            "blue"
          )}
          {renderSuggestionItem(
            <Percent className="w-4 h-4 text-purple-400" />,
            "Best Odds Range",
            data?.odds_range,
            "purple"
          )}
          {data?.tipster?.has_data && renderSuggestionItem(
            <Users className="w-4 h-4 text-orange-400" />,
            "Best Tipster",
            data?.tipster,
            "orange"
          )}
          {data?.strategy?.has_data && renderSuggestionItem(
            <BookOpen className="w-4 h-4 text-cyan-400" />,
            "Best Strategy",
            data?.strategy,
            "cyan"
          )}
        </div>

        {/* Expand/Collapse Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 text-gray-400 hover:text-white hover:bg-white/5"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Hide Detailed Analysis
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-2" />
              Show Detailed Analysis
            </>
          )}
        </Button>

        {/* Expanded Detailed View */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-6">
            {renderDetailedStats("Market", data?.market, <Target className="w-4 h-4 text-green-400" />)}
            {renderDetailedStats("League", data?.league, <TrendingUp className="w-4 h-4 text-blue-400" />)}
            {renderDetailedStats("Odds Range", data?.odds_range, <Percent className="w-4 h-4 text-purple-400" />)}
            {data?.tipster?.has_data && renderDetailedStats("Tipster", data?.tipster, <Users className="w-4 h-4 text-orange-400" />)}
            {data?.strategy?.has_data && renderDetailedStats("Strategy", data?.strategy, <BookOpen className="w-4 h-4 text-cyan-400" />)}
          </div>
        )}

        {/* Footer info */}
        <p className="text-xs text-gray-600 mt-4 text-center">
          Suggestions based on your betting history • Updates automatically as you track bets
        </p>
      </CardContent>
    </Card>
  );
};

export default BettingSuggestions;
